import { NavLink } from "react-router"
import { useEffect } from "react"
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "~/components/ui/sidebar"
import { BillingContextCookie } from "~/server/cookies.server"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu"
import { CaretSortIcon, ExitIcon, ShuffleIcon } from "@radix-ui/react-icons"
import {
  Activity,
  ArrowUpRight,
  CircleUserIcon,
  IdCardIcon,
  Megaphone,
  Users,
} from "lucide-react"
import { useAuth } from "~/contexts/AuthContext"
import {
  identifyWithContext,
  resetPosthogIdentity,
} from "~/client/posthog.client"
import { getRoleIcon } from "~/utils/roles"
import { RELEASE_LOG_URL } from "~/lib/release-announcements"

export function SidebarUserMenu({
  billingContext,
  roles,
  hasOperatingOrgAccess,
  userAccountCount,
}: {
  billingContext: BillingContextCookie
  roles: string[]
  hasOperatingOrgAccess?: boolean
  userAccountCount?: number
}) {
  const { user, logout: authLogout } = useAuth()

  // Identify user with PostHog when user logs in or billing context changes
  useEffect(() => {
    if (!user?.uid) return
    identifyWithContext(
      user.uid,
      { email: user.email, displayName: user.displayName },
      {
        accountId: billingContext.id || null,
        accountName: billingContext.displayName || null,
        accountKind: billingContext.kind || null,
      },
    )
  }, [
    user?.uid,
    user?.email,
    user?.displayName,
    billingContext?.id,
    billingContext?.displayName,
    billingContext?.kind,
  ])

  const handleLogout = async () => {
    try {
      resetPosthogIdentity()
      await authLogout()
    } catch (error) {
      console.error("Logout error:", error)
    }
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg">
              {user?.photoURL ? (
                <img
                  src={user?.photoURL || undefined}
                  alt={user?.displayName || ""}
                  className="rounded-full w-8 h-8"
                />
              ) : (
                <CircleUserIcon className="!h-8 !w-8 " strokeWidth={1} />
              )}
              <span>
                <div className="flex flex-col">
                  <span>{user?.displayName || ""}</span>

                  <span className="text-xs">{billingContext.displayName}</span>
                </div>
              </span>
              <CaretSortIcon className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="right"
            className="min-w-fit border bg-accent text-accent-foreground p-2 mb-2 rounded-sm drop-shadow-md"
          >
            {roles.length > 0 && (
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase border-b mb-1">
                <div className="flex flex-wrap gap-2 items-center">
                  {(roles.includes("Admin") ? ["Admin"] : roles).map((role) => {
                    const Icon = getRoleIcon(role)
                    return (
                      <span
                        key={role}
                        className="flex items-center gap-1.5 uppercase"
                      >
                        {Icon && <Icon className="h-3 w-3" strokeWidth={2} />}
                        {role}
                      </span>
                    )
                  })}
                </div>
              </div>
            )}
            {roles.includes("Admin") && (
              <DropdownMenuItem asChild>
                <NavLink to="/manage-users">
                  <SidebarMenuButton className="w-full justify-start">
                    <Users className="mr-2 h-4 w-4" />
                    <span>Manage Users</span>
                  </SidebarMenuButton>
                </NavLink>
              </DropdownMenuItem>
            )}
            {(roles.includes("Admin") || roles.includes("Data Producer")) && (
              <DropdownMenuItem asChild>
                <NavLink to="/all-dataset-status">
                  <SidebarMenuButton className="w-full justify-start">
                    <Activity className="mr-2 h-4 w-4" />
                    <span>All Dataset Status</span>
                  </SidebarMenuButton>
                </NavLink>
              </DropdownMenuItem>
            )}
            {hasOperatingOrgAccess && (
              <DropdownMenuItem asChild>
                <NavLink to="/accounts">
                  <SidebarMenuButton className="w-full justify-start">
                    <IdCardIcon className="mr-2 h-4 w-4" />
                    <span>Manage Accounts</span>
                  </SidebarMenuButton>
                </NavLink>
              </DropdownMenuItem>
            )}
            {typeof userAccountCount === "number" && userAccountCount > 1 && (
              <DropdownMenuItem asChild>
                <NavLink to="/select-account">
                  <SidebarMenuButton className="w-full justify-start">
                    <ShuffleIcon className="mr-2" />
                    <span>Select Account</span>
                  </SidebarMenuButton>
                </NavLink>
              </DropdownMenuItem>
            )}
            {/* The Release Log is its own page: it opens in a new tab, and the
                arrow says so. */}
            <DropdownMenuItem asChild>
              <a
                href={`${RELEASE_LOG_URL}?from=console`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <SidebarMenuButton className="w-full justify-start">
                  <Megaphone className="mr-2 h-4 w-4" />
                  <span>Release Log</span>
                  <ArrowUpRight
                    className="ml-auto h-4 w-4 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="sr-only">(opens in a new tab)</span>
                </SidebarMenuButton>
              </a>
            </DropdownMenuItem>
            {/* Keeps Logout apart, so a quick click can't land on it by mistake. */}
            <DropdownMenuSeparator className="my-1 bg-primary/15" />
            <DropdownMenuItem>
              <SidebarMenuButton
                className="w-full justify-start"
                onClick={handleLogout}
              >
                <ExitIcon className="mr-2" />
                <span>Logout</span>
              </SidebarMenuButton>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
